import { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { API, axios, showToast } from './adminHelpers';

export const TrackingManager = ({ authHeader }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/admin/settings`, { headers: authHeader });
        setSettings(res.data);
      } catch (err) {
        console.error('Failed to fetch settings:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [authHeader]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, settings, { headers: authHeader });
      showToast('Tracking settings saved!');
    } catch {
      showToast('Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const updateTracking = (field, value) => {
    setSettings(prev => ({ ...prev, tracking: { ...prev?.tracking, [field]: value } }));
  };

  const updateCustomCode = (field, value) => {
    setSettings(prev => ({ ...prev, custom_code: { ...prev?.custom_code, [field]: value } }));
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="tracking-manager">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Tracking & Analytics</h1>
        <Button onClick={saveSettings} disabled={saving} className="bg-primary hover:bg-primary-hover" data-testid="save-tracking-btn">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="font-semibold mb-4">Google Analytics</h2>
          <div className="space-y-4">
            <div>
              <Label>GA4 Measurement ID</Label>
              <Input value={settings?.tracking?.ga4_measurement_id || ''} onChange={(e) => updateTracking('ga4_measurement_id', e.target.value)} placeholder="G-XXXXXXXXXX" className="mt-1" data-testid="tracking-ga4" />
            </div>
            <div>
              <Label>GTM Container ID</Label>
              <Input value={settings?.tracking?.gtm_container_id || ''} onChange={(e) => updateTracking('gtm_container_id', e.target.value)} placeholder="GTM-XXXXXXX" className="mt-1" data-testid="tracking-gtm" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="font-semibold mb-4">Google Ads</h2>
          <div className="space-y-4">
            <div>
              <Label>Google Ads ID</Label>
              <Input value={settings?.tracking?.google_ads_id || ''} onChange={(e) => updateTracking('google_ads_id', e.target.value)} placeholder="AW-XXXXXXXXXX" className="mt-1" data-testid="tracking-ads-id" />
            </div>
            <div>
              <Label>Conversion ID</Label>
              <Input value={settings?.tracking?.google_ads_conversion_id || ''} onChange={(e) => updateTracking('google_ads_conversion_id', e.target.value)} placeholder="Conversion label" className="mt-1" data-testid="tracking-conversion" />
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="font-semibold mb-4">Custom Code</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div>
            <Label>Header Code</Label>
            <Textarea value={settings?.custom_code?.head_code || ''} onChange={(e) => updateCustomCode('head_code', e.target.value)} placeholder="Scripts to add in <head>" rows={6} className="mt-1 font-mono text-xs" data-testid="custom-head-code" />
          </div>
          <div>
            <Label>Body Start Code</Label>
            <Textarea value={settings?.custom_code?.body_start_code || ''} onChange={(e) => updateCustomCode('body_start_code', e.target.value)} placeholder="Scripts after <body>" rows={6} className="mt-1 font-mono text-xs" data-testid="custom-body-start" />
          </div>
          <div>
            <Label>Body End Code</Label>
            <Textarea value={settings?.custom_code?.body_end_code || ''} onChange={(e) => updateCustomCode('body_end_code', e.target.value)} placeholder="Scripts before </body>" rows={6} className="mt-1 font-mono text-xs" data-testid="custom-body-end" />
          </div>
        </div>
      </div>
    </div>
  );
};
